import { getDocuments } from 'api/operations/documentsService';
import { applyProposalUserAction } from 'api/operations/enrollmentService';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { USER_ACTIONS } from 'types/enrollmentService';
import { Document, UploadStep } from 'types/documentsService';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { UploadResponseDocumentModalStore } from 'store/pages/rfq/rfq-response/proposal-response/uploadResponseDocumentModalStore';
import ResponseDocumentDelegate from './responseDocumentDelegate';

interface UploadDocumentsDomainData {
  // Cart id
  entityId: string;
  // Period id
  affiliatedId: string;
}
export default class RFQProposalResponseStore {
  private _isLoading = false;
  private _documents: ResponseDocumentDelegate[] = [];
  private readonly _domainData: UploadDocumentsDomainData;
  private readonly _enrollmentPeriodModule = new EnrollmentPeriodModule();

  // Ui stores
  public uploadDocumentModalStore: UploadResponseDocumentModalStore | null = null;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get activeDocuments(): ResponseDocumentDelegate[] {
    return this._documents.filter((docDelegate) => docDelegate.document.active === true);
  }

  private get documentsTitles(): string[] {
    return Array.from(new Set(this.activeDocuments.map((documentDelegate) => documentDelegate.document.type)));
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public async getInitialData(): Promise<void> {
    this.setIsLoading(true);
    await this._uploadEnrollmentData();
    await runInAction(async () => {
      const documents = await getDocuments(
        this._domainData.entityId,
        UploadStep.RFQ_RESPONSE,
        this._domainData.affiliatedId,
      );

      runInAction(() => {
        this._createDocumentDelegates(documents);
        this._createDocumentUploadModalStore();
        this.setIsLoading(false);
      });
    });
  }

  private async _uploadEnrollmentData(): Promise<void> {
    // Don't re-request enrollment period
    if (this._enrollmentPeriodModule.enrollmentCoreData?.id) return;

    await this._enrollmentPeriodModule.uploadEnrollmentPeriod(
      this._domainData.affiliatedId || this._domainData.entityId,
    );
  }

  public async completeStep(periodId: string, cartId: string): Promise<void> {
    this.setIsLoading(true);
    await applyProposalUserAction(periodId, cartId, USER_ACTIONS.COMPLETE_PROPOSAL_RESPONSE);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  public getExistingDocumentTypes(): string[] {
    return this.documentsTitles;
  }

  private _createDocumentUploadModalStore(): void {
    if (!this._enrollmentPeriodModule.enrollmentCoreData) {
      throw new Error('Attempted to create a document upload modal store without enrollment period loaded!');
    }
    this.uploadDocumentModalStore = new UploadResponseDocumentModalStore({
      entityId: this._domainData.entityId,
      onSubmit: this.getInitialData,
      setPageIsLoading: this.setIsLoading,
      getExistingDocumentTypes: this.getExistingDocumentTypes,
    });
  }

  private _createDocumentDelegates(documents: Document[]): void {
    this._documents = documents.map((document) => {
      return new ResponseDocumentDelegate({
        document,
        setPageIsLoading: this.setIsLoading,
        refreshData: this.getInitialData,
        getExistingDocumentTypes: this.getExistingDocumentTypes,
      });
    });
  }

  constructor(domainData: UploadDocumentsDomainData) {
    this._domainData = domainData;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(
  new RFQProposalResponseStore({ entityId: 'stub-entity', affiliatedId: 'stub-affiliation' }),
);

export const useRFQProposalResponseStore = (): RFQProposalResponseStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
